import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  X,
  UploadCloud,
  Image as ImageIcon,
  Video as VideoIcon,
  Link as LinkIcon,
  Crown,
  Star,
  PartyPopper,
  UtensilsCrossed,
  Camera,
  Trophy,
  Save,
  AlertCircle,
} from "lucide-react";
import {
  adminListErfolge,
  adminCreateErfolge,
  adminUpdateErfolge,
  adminDeleteErfolge,
  adminReorderErfolge,
  adminUpload,
  resolveAssetUrl,
} from "../../lib/api";
import { parseEmbed } from "../../lib/embed";

const CATEGORIES = [
  { id: "premium", label: "Premium Brands", icon: Crown },
  { id: "stars", label: "Stars", icon: Star },
  { id: "clubs", label: "Clubs / Events", icon: PartyPopper },
  { id: "gastronomie", label: "Gastronomie", icon: UtensilsCrossed },
  { id: "fotos", label: "Fotos", icon: Camera },
  { id: "ugc", label: "UGC Content", icon: VideoIcon },
  { id: "sport", label: "Sport", icon: Trophy },
];

const TYPES = [
  { id: "brand", label: "Logo / Brand", desc: "Markenlogo + Titel + Untertitel" },
  { id: "photo", label: "Foto", desc: "Bild-Upload" },
  { id: "video", label: "Video", desc: "Video-Upload" },
  { id: "external", label: "Externer Link", desc: "TikTok / Instagram URL" },
];

const emptyItem = (category) => ({
  category,
  type: "brand",
  title: "",
  subtitle: "",
  metric: "",
  src: "",
  logo_bg: "dark",
  platform: "",
  url: "",
  gradient: "",
});

const FileDropzone = ({ accept, onFile, busy, progress }) => {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
        drag
          ? "border-pink-400 bg-pink-500/10"
          : "border-white/15 bg-white/5 hover:border-pink-400/50 hover:bg-white/10"
      }`}
      data-testid="file-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      <UploadCloud size={28} className="mx-auto text-pink-400 mb-2" />
      <div className="text-sm font-bold text-white">
        {busy ? `Hochladen… ${progress}%` : "Datei hier ablegen oder klicken"}
      </div>
      <div className="text-xs text-neutral-400 mt-1">
        {accept?.includes("video")
          ? "MP4 / MOV / WebM (max. 250 MB)"
          : "JPG / PNG / WEBP / SVG (max. 250 MB)"}
      </div>
      {busy && (
        <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

const ItemPreview = ({ item }) => {
  const url = resolveAssetUrl(item.src);
  if (item.type === "video") {
    return url ? (
      <video src={url} className="w-16 h-16 rounded-lg object-cover bg-black" muted />
    ) : (
      <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center">
        <VideoIcon size={20} className="text-neutral-500" />
      </div>
    );
  }
  if (item.type === "external") {
    return (
      <div className="w-16 h-16 rounded-lg flex items-center justify-center bg-gradient-to-br from-pink-500 via-orange-500 to-purple-500">
        <LinkIcon size={20} className="text-white" />
      </div>
    );
  }
  // brand or photo (image)
  return url ? (
    <div
      className={`w-16 h-16 rounded-lg p-1.5 flex items-center justify-center ${
        item.type === "brand" && item.logo_bg === "light"
          ? "bg-white"
          : item.type === "brand"
          ? "bg-neutral-800"
          : "bg-black"
      }`}
    >
      <img
        src={url}
        alt={item.title}
        className="max-w-full max-h-full object-contain"
      />
    </div>
  ) : (
    <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center">
      <ImageIcon size={20} className="text-neutral-500" />
    </div>
  );
};

const ItemForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (file) => {
    setUploading(true);
    setProgress(0);
    setErr("");
    try {
      const res = await adminUpload(file, setProgress);
      setField("src", res.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form };
      const saved = initial.id
        ? await adminUpdateErfolge(initial.id, payload)
        : await adminCreateErfolge(payload);
      onSaved(saved);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const isEdit = !!initial.id;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-6"
      onClick={onClose}
      data-testid="erfolge-form-overlay"
    >
      <div
        className="w-full lg:max-w-2xl bg-neutral-950 border border-white/10 rounded-t-3xl lg:rounded-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-neutral-950 px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-pink-400">
              {isEdit ? "Bearbeiten" : "Neuer Eintrag"}
            </div>
            <div className="font-display font-extrabold text-xl text-white">
              {CATEGORIES.find((c) => c.id === form.category)?.label}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"
            data-testid="erfolge-form-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Type selector */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
              Typ
            </div>
            <div className="grid grid-cols-2 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setField("type", t.id)}
                  className={`p-3 rounded-xl text-left border transition-colors ${
                    form.type === t.id
                      ? "bg-gradient-to-r from-pink-500/20 via-orange-500/20 to-purple-500/20 border-pink-400/60"
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                  }`}
                  data-testid={`type-btn-${t.id}`}
                >
                  <div className="text-sm font-bold text-white">{t.label}</div>
                  <div className="text-[11px] text-neutral-400 mt-0.5">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Title + subtitle */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                Titel
              </label>
              <input
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="z. B. Aldi Süd"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500"
                data-testid="form-title"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                Untertitel
              </label>
              <input
                value={form.subtitle}
                onChange={(e) => setField("subtitle", e.target.value)}
                placeholder="z. B. Premium Brand"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500"
                data-testid="form-subtitle"
              />
            </div>
          </div>

          {/* Metric (optional) */}
          {(form.type === "video" || form.type === "external") && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                Metric / Badge (optional)
              </label>
              <input
                value={form.metric}
                onChange={(e) => setField("metric", e.target.value)}
                placeholder="z. B. 3.0M Views"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500"
                data-testid="form-metric"
              />
            </div>
          )}

          {/* External fields */}
          {form.type === "external" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                    Plattform
                  </label>
                  <select
                    value={form.platform}
                    onChange={(e) => setField("platform", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white"
                    data-testid="form-platform"
                  >
                    <option value="" className="bg-neutral-900">– wählen –</option>
                    <option value="TikTok" className="bg-neutral-900">TikTok</option>
                    <option value="Instagram" className="bg-neutral-900">Instagram</option>
                    <option value="YouTube" className="bg-neutral-900">YouTube</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                    URL
                  </label>
                  <input
                    value={form.url}
                    onChange={(e) => setField("url", e.target.value)}
                    placeholder="https://www.tiktok.com/..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500"
                    data-testid="form-url"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-1">
                  Gradient (CSS, optional)
                </label>
                <input
                  value={form.gradient}
                  onChange={(e) => setField("gradient", e.target.value)}
                  placeholder="linear-gradient(135deg, #ff0050 0%, #000 50%, #00f2ea 100%)"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500 font-mono text-xs"
                />
              </div>

              {/* Live embed preview */}
              {(() => {
                const meta = parseEmbed(form.url);
                if (!meta) return null;
                return (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-2">
                      Vorschau – {meta.label}
                    </label>
                    <div className="rounded-xl overflow-hidden bg-black ring-1 ring-white/10">
                      <div
                        className={`w-full ${meta.aspect === "16/9" ? "aspect-video" : "aspect-[9/16]"} max-h-[520px]`}
                      >
                        <iframe
                          src={meta.embedUrl}
                          title="Preview"
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          className="w-full h-full"
                          style={{ border: 0 }}
                        />
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-neutral-500">
                      Plattform & Thumbnail werden automatisch aus dem Link erkannt.
                    </p>
                  </div>
                );
              })()}
            </>
          )}

          {/* Brand background */}
          {form.type === "brand" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-2">
                Logo-Hintergrund
              </label>
              <div className="flex gap-2">
                {[
                  { id: "dark", label: "Dunkel" },
                  { id: "light", label: "Hell" },
                ].map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setField("logo_bg", b.id)}
                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                      form.logo_bg === b.id
                        ? "bg-white text-neutral-950 border-white"
                        : "bg-white/5 text-neutral-300 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Source */}
          {form.type !== "external" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-2">
                {form.type === "video" ? "Video" : form.type === "photo" ? "Foto" : "Logo"}
              </label>
              {form.src ? (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                  <ItemPreview item={form} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-neutral-400 truncate">
                      {form.src}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setField("src", "")}
                    className="p-2 rounded-full text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
                    aria-label="Entfernen"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <FileDropzone
                  accept={form.type === "video" ? "video/*" : "image/*"}
                  onFile={handleUpload}
                  busy={uploading}
                  progress={progress}
                />
              )}
              <div className="mt-2">
                <input
                  value={typeof form.src === "string" && form.src.startsWith("http") ? form.src : ""}
                  onChange={(e) => setField("src", e.target.value)}
                  placeholder="…oder URL einfügen (z. B. https://…)"
                  className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 outline-none text-white placeholder:text-neutral-500 text-xs"
                />
              </div>
            </div>
          )}

          {err && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2">
              <AlertCircle size={14} />
              {err}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={save}
              disabled={saving || uploading}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold uppercase tracking-wider hover:-translate-y-[2px] transition-all disabled:opacity-60"
              data-testid="erfolge-form-save"
            >
              <Save size={14} />
              {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ErfolgePage = () => {
  const [activeCat, setActiveCat] = useState("premium");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const dragId = useRef(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await adminListErfolge({ category: activeCat });
      setItems(data.items || []);
    } catch (e) {
      setErr("Konnte Einträge nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeCat]);

  const counts = useMemo(() => {
    const c = {};
    items.forEach((i) => (c[i.category] = (c[i.category] || 0) + 1));
    return c;
  }, [items]);

  const onSaved = (saved) => {
    setItems((prev) => {
      const exists = prev.find((p) => p.id === saved.id);
      if (exists) return prev.map((p) => (p.id === saved.id ? saved : p));
      return [...prev, saved];
    });
    setEditing(null);
  };

  const remove = async (item) => {
    if (!window.confirm(`„${item.title || "Eintrag"}“ wirklich löschen?`)) return;
    try {
      await adminDeleteErfolge(item.id);
      setItems((prev) => prev.filter((p) => p.id !== item.id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const onDragStart = (id) => (dragId.current = id);

  const onDragOver = (e) => e.preventDefault();

  const onDrop = async (targetId) => {
    const sourceId = dragId.current;
    dragId.current = null;
    if (!sourceId || sourceId === targetId) return;
    const reordered = [...items];
    const fromIdx = reordered.findIndex((i) => i.id === sourceId);
    const toIdx = reordered.findIndex((i) => i.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setItems(reordered);
    try {
      await adminReorderErfolge(
        activeCat,
        reordered.map((r) => r.id)
      );
    } catch {
      load();
    }
  };

  return (
    <div data-testid="admin-erfolge-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-pink-400">
            Inhalte
          </div>
          <h1 className="mt-1 font-display font-extrabold text-3xl md:text-5xl tracking-tight">
            Erfolge verwalten
          </h1>
          <p className="mt-2 text-sm text-neutral-400 max-w-xl">
            Bilder, Videos & Logos je Kategorie hochladen. Per Drag & Drop sortieren.
            Änderungen sind sofort live auf der Erfolge-Seite.
          </p>
        </div>
        <button
          onClick={() => setEditing(emptyItem(activeCat))}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white text-sm font-bold uppercase tracking-wider hover:-translate-y-[2px] transition-all"
          data-testid="erfolge-add-btn"
        >
          <Plus size={14} /> Neuer Eintrag
        </button>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((c) => {
          const Ico = c.icon;
          const isActive = activeCat === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-wider uppercase border transition-colors ${
                isActive
                  ? "bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 text-white border-transparent"
                  : "bg-white/5 border-white/10 text-neutral-300 hover:bg-white/10"
              }`}
              data-testid={`erfolge-tab-${c.id}`}
            >
              <Ico size={13} />
              {c.label}
              {counts[c.id] != null && isActive && (
                <span className="opacity-80">({counts[c.id]})</span>
              )}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="mb-4 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {err}
        </div>
      )}

      <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/5">
        {loading && (
          <div className="p-8 text-center text-sm text-neutral-400">Laden…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center">
            <div className="text-sm text-neutral-400">
              Noch keine Einträge in dieser Kategorie.
            </div>
            <button
              onClick={() => setEditing(emptyItem(activeCat))}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider"
            >
              <Plus size={12} /> Ersten Eintrag erstellen
            </button>
          </div>
        )}
        {!loading &&
          items.map((item) => (
            <div
              key={item.id}
              draggable
              onDragStart={() => onDragStart(item.id)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(item.id)}
              className="flex items-center gap-3 p-3 md:p-4 hover:bg-white/5 transition-colors group"
              data-testid={`erfolge-row-${item.id}`}
            >
              <div className="text-neutral-600 group-hover:text-neutral-300 cursor-grab active:cursor-grabbing">
                <GripVertical size={18} />
              </div>
              <ItemPreview item={item} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-white truncate">
                    {item.title || <span className="text-neutral-500">(ohne Titel)</span>}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/5 text-neutral-300 border border-white/10">
                    {item.type}
                  </span>
                  {item.is_default && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/30">
                      Default
                    </span>
                  )}
                </div>
                <div className="text-xs text-neutral-400 truncate mt-0.5">
                  {item.subtitle}
                  {item.metric ? ` · ${item.metric}` : ""}
                  {item.platform ? ` · ${item.platform}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditing(item)}
                  className="p-2 rounded-full text-neutral-400 hover:text-white hover:bg-white/10"
                  aria-label="Bearbeiten"
                  data-testid={`erfolge-edit-${item.id}`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => remove(item)}
                  className="p-2 rounded-full text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
                  aria-label="Löschen"
                  data-testid={`erfolge-delete-${item.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
      </div>

      {editing && (
        <ItemForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default ErfolgePage;
